import { useState } from 'react';
import axios from 'axios';
import { AUTH_URL } from '../utils/authApi';

// Regel 7 van kb/patterns/havun-mobile-login.md: na een magic-link-login op een
// toestel met platform-authenticator actief biometrie voorstellen. Dat moment is
// het beste dat er is -- de gebruiker heeft zojuist bewezen wie hij is en houdt
// het toestel in zijn hand. Zonder dit voorstel groeit biometrie niet: koppelen
// kon alleen via de onboarding van een nieuw account.

export const DECLINED_KEY = 'bio_prompt_declined';

function base64urlToBuffer(base64url) {
  const padding = '='.repeat((4 - base64url.length % 4) % 4);
  const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/') + padding;
  const rawData = atob(base64);
  return Uint8Array.from(rawData, c => c.charCodeAt(0)).buffer;
}

function bufferToBase64url(buffer) {
  const bytes = new Uint8Array(buffer);
  let str = '';
  for (const byte of bytes) str += String.fromCharCode(byte);
  return btoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
}

function defaultDeviceName() {
  const ua = navigator.userAgent;
  if (ua.includes('iPhone')) return 'iPhone';
  if (ua.includes('iPad')) return 'iPad';
  if (ua.includes('Android')) return 'Android telefoon';
  if (ua.includes('Mac')) return 'Mac';
  if (ua.includes('Windows')) return 'Windows PC';
  return 'Mijn apparaat';
}

export function BiometricOffer({ token, onDone }) {
  const [error, setError] = useState('');
  const [isRegistering, setIsRegistering] = useState(false);

  const decline = () => {
    // Eén keer nee is nee. Elke login opnieuw vragen maakt van een aanbod een
    // hindernis.
    localStorage.setItem(DECLINED_KEY, '1');
    onDone();
  };

  const register = async () => {
    setError('');
    setIsRegistering(true);

    try {
      const optionsResponse = await axios.get(
        `${AUTH_URL}/api/auth/webauthn/register-options`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const options = optionsResponse.data;

      const credential = await navigator.credentials.create({
        publicKey: {
          challenge: base64urlToBuffer(options.challenge),
          rp: options.rp,
          user: {
            id: base64urlToBuffer(options.user.id),
            name: options.user.name,
            displayName: options.user.displayName,
          },
          pubKeyCredParams: options.pubKeyCredParams,
          timeout: options.timeout,
          attestation: options.attestation,
          authenticatorSelection: options.authenticatorSelection,
        },
      });

      await axios.post(
        `${AUTH_URL}/api/auth/webauthn/register`,
        {
          credential: {
            id: credential.id,
            rawId: bufferToBase64url(credential.rawId),
            type: credential.type,
            response: {
              attestationObject: bufferToBase64url(credential.response.attestationObject),
              clientDataJSON: bufferToBase64url(credential.response.clientDataJSON),
            },
          },
          device_name: defaultDeviceName(),
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      onDone();
    } catch (err) {
      // Serverfouten leesbaar houden (regel 6) -- nooit `[object Response]`.
      setError(err.name === 'NotAllowedError'
        ? 'Koppelen geannuleerd.'
        : (err.response?.data?.message || 'Koppelen is niet gelukt.'));
      setIsRegistering(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-linear-to-br from-primary-50 to-primary-100 dark:from-gray-900 dark:to-gray-800">
      <div className="max-w-md w-full mx-4">
        <div className="card dark:bg-gray-800 rounded-2xl shadow-xl p-8 space-y-6">
          <div className="text-center">
            <span className="text-5xl">🔐</span>
            <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mt-4">
              Volgende keer met je vingerafdruk?
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
              Dan hoef je niet meer op een e-mail te wachten. Je koppelt dit toestel één keer.
            </p>
          </div>

          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          <button
            type="button"
            onClick={register}
            disabled={isRegistering}
            className="w-full btn-primary py-4 flex items-center justify-center gap-3 text-lg disabled:opacity-50"
          >
            {isRegistering ? 'Koppelen...' : 'Biometrie koppelen'}
          </button>

          <button type="button" onClick={decline} className="w-full btn-secondary">
            Niet nu
          </button>
        </div>
      </div>
    </div>
  );
}
